// @ts-nocheck
import React, { useRef, useState } from 'react';
import { UploadIcon, XIcon } from './icons';

/**
 * FileUpload — dropzone primitive matching the reference `.upload` style.
 *
 * Controlled: pass `files` (File[]) and `onChange(files)`. Pure UI; uploading
 * is the caller's responsibility.
 *
 *   <FileUpload
 *     files={state.files}
 *     onChange={(files) => setState({ files })}
 *     accept=".csv"
 *     maxSize={10 * 1024 * 1024}
 *     maxFiles={1}
 *     note="Max 1 CSV, < 10MB."
 *   />
 */
const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const FileUpload = ({
  files = [],
  onChange,
  accept = '',
  maxSize,
  maxFiles = 1,
  note,
  disabled,
  className = '',
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);

  const validate = (list) => {
    if (maxFiles && list.length > maxFiles) {
      setError(`Maximum of ${maxFiles} file${maxFiles === 1 ? '' : 's'} allowed.`);
      return false;
    }
    if (maxSize) {
      const over = list.find((f) => f.size > maxSize);
      if (over) {
        setError(`${over.name} exceeds ${formatBytes(maxSize)}.`);
        return false;
      }
    }
    if (accept) {
      const extensions = accept.split(',').map((s) => s.trim().toLowerCase()).filter((s) => s.startsWith('.'));
      if (extensions.length) {
        const bad = list.find((f) => !extensions.some((ext) => f.name.toLowerCase().endsWith(ext)));
        if (bad) {
          setError(`${bad.name} type not allowed. Accepted: ${extensions.join(', ')}`);
          return false;
        }
      }
    }
    setError(null);
    return true;
  };

  const accept_ = (next) => {
    const merged = maxFiles === 1 ? next : [...files, ...next].slice(0, maxFiles || undefined);
    if (validate(merged)) onChange && onChange(merged);
  };

  const onPick = (e) => {
    const next = Array.from(e.target.files || []);
    accept_(next);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const next = Array.from(e.dataTransfer.files || []);
    accept_(next);
  };

  const removeAt = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    onChange && onChange(next);
    setError(null);
  };

  return (
    <div className={className}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={[
          'w-full text-center cursor-pointer mov-focus-ring outline-none',
          'transition-[border-color,background] duration-150',
        ].join(' ')}
        style={{
          border: `1.5px dashed ${dragOver ? 'var(--mov-accent)' : 'var(--mov-line-strong)'}`,
          borderRadius: 12,
          padding: '22px 18px',
          color: 'var(--mov-ink-soft)',
          background: 'repeating-linear-gradient(135deg, transparent 0 8px, rgba(20,24,26,0.02) 8px 9px)',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <span
          aria-hidden="true"
          className="grid place-items-center mx-auto mb-2.5"
          style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'var(--mov-accent-soft)', color: 'var(--mov-accent-ink)',
          }}
        >
          <UploadIcon size={16} />
        </span>
        <div className="text-[13px]">
          <b style={{ color: 'var(--mov-ink)' }}>Drop files</b> here or click to browse
        </div>
        {note && (
          <div className="mov-mono mt-1.5" style={{ fontSize: 10.5, color: 'var(--mov-ink-mute)' }}>
            {note}
          </div>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={maxFiles !== 1}
        onChange={onPick}
        style={{ display: 'none' }}
      />

      {error && (
        <div className="mt-2 text-[12px]" style={{ color: 'var(--mov-danger-ink)' }}>{error}</div>
      )}

      {files.length > 0 && (
        <div className="mt-3 flex flex-col gap-2">
          {files.map((f, idx) => (
            <FileChip
              // eslint-disable-next-line react/no-array-index-key
              key={`${f.name}-${idx}`}
              file={f}
              onRemove={() => removeAt(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FileChip = ({ file, onRemove }) => {
  const ext = (file.name.split('.').pop() || 'file').toUpperCase().slice(0, 4);
  return (
    <div
      className="flex items-center gap-2.5"
      style={{
        border: '1px solid var(--mov-line)',
        background: 'var(--mov-paper-2)',
        borderRadius: 10,
        padding: '10px 12px',
      }}
    >
      <span
        className="mov-mono grid place-items-center shrink-0"
        style={{
          width: 32, height: 32, borderRadius: 7,
          background: 'var(--mov-chip-gray-bg)', color: 'var(--mov-chip-gray-fg)',
          fontSize: 10, fontWeight: 600,
        }}
      >
        {ext}
      </span>
      <div className="flex-1 min-w-0">
        <b className="block text-[13px] truncate" style={{ color: 'var(--mov-ink)' }}>
          {file.name}
        </b>
        <small className="text-[11.5px]" style={{ color: 'var(--mov-ink-mute)' }}>
          {formatBytes(file.size)}
        </small>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove"
        className="shrink-0"
        style={{
          background: 'transparent', border: 0, padding: 4,
          color: 'var(--mov-ink-mute)', cursor: 'pointer',
        }}
      >
        <XIcon size={14} />
      </button>
    </div>
  );
};

export default FileUpload;
